<script>
import {defineComponent, ref, onBeforeMount, nextTick, reactive, computed} from "vue";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import Column from "primevue/column";
import {useApiCall} from "@/composables/useApiCall";
import {createOrdersApiCall, deleteOrdersApiCall, geoRestrictionsApiCall, ordersApiCall} from "@/services/yandex";
import {useI18n} from "vue-i18n";
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";
import Calendar from "primevue/calendar";
import Dropdown from "primevue/dropdown";

export default defineComponent({
  layout: { name: 'AdminLayout' },
  components: {Dropdown, Calendar, Checkbox, Column, ConfirmationModal, DataTable, Button, InputText},
  setup() {
    const { t } = useI18n()
    const visibleDeleteModal = ref(false)
    const visibleCreateModal = ref(false)
    const deleteId = ref(0)
    const fields = reactive({
      value: '',
      name: '',
      count: '',
      active_until: '',
      geo_restrictions: [
        {
          max_intermediate_points: 0,
          source: {
            geo_restriction_id: '',
          },
          destination: {
            geo_restriction_id: '',
          }
        }
      ]
    })
    const isValid = computed(() => fields.value && fields.active_until && fields.count && fields.geo_restrictions[0].source.geo_restriction_id && fields.geo_restrictions[0].destination.geo_restriction_id)
    const orders = ref([])
    const today = computed(() => {
      return new Date()
    })

    const georRestrictions = ref([])

    const toggleDeleteModal = (id = 0) => {
      deleteError.value = null
      visibleDeleteModal.value = !visibleDeleteModal.value
      if (id) {
        deleteId.value = id
      } else {
        deleteId.value = 0
      }
    }

    const toggleCreateModal = () => {
      createError.value = null
      errorForm.value = null
      visibleCreateModal.value = !visibleCreateModal.value
      if (!visibleCreateModal.value) {
        fields.value = ''
        fields.name = ''
        fields.count = ''
        fields.active_until = ''
        fields.geo_restrictions[0] = {
          max_intermediate_points: 0,
          source: {
            geo_restriction_id: '',
          },
          destination: {
            geo_restriction_id: '',
          }
        }
      }
    }

    const {
      isLoading: isOrdersLoading,
      data: ordersData,
      error: ordersError,
      executeApiCall: getOrdersAction,
    } = useApiCall(ordersApiCall, true)

    const {
      isLoading: isGeoLoading,
      data: geoData,
      error: geoError,
      executeApiCall: getGeoAction,
    } = useApiCall(geoRestrictionsApiCall, true)

    const {
      isLoading: isCreateLoading,
      data: createData,
      error: createError,
      executeApiCall: createAction,
    } = useApiCall(createOrdersApiCall, true)

    const {
      isLoading: isDeleteLoading,
      data: deleteData,
      error: deleteError,
      executeApiCall: deleteAction,
    } = useApiCall(deleteOrdersApiCall, true)

    const types = ref([
      {
        name: 'Такси',
        type: 'taxi',
      },
      {
        name: 'Лавка',
        type: 'grocery',
      },
      {
        name: 'Еда',
        type: 'eats',
      },
      {
        name: 'Заправки',
        type: 'fuel',
      },
      {
        name: 'Карго',
        type: 'cargo',
      },
    ])

    const currentType = ref('taxi')

    const getNameService = (value) => {
      switch (value) {
          case 'taxi': return 'Такси'
          case 'grocery': return 'Лавка'
          case 'eats': return 'Еда'
          case 'fuel': return 'Заправки'
          case 'cargo': return 'Карго'
          default: return ''
      }
    }

    const loadOrders = async () => {
      try {
        await getOrdersAction()
        if (ordersData.value) {
          orders.value = [ ...ordersData.value.data.orders ]
        }
      } catch {
        console.error(ordersError.value)
      }
    }
    const loadGeos = async () => {
      try {
        await getGeoAction()
        if (geoData.value) {
          georRestrictions.value = [ ...geoData.value.data.items ]
        }
      } catch {
        console.error(geoError.value )
      }
    }
    const deleteItem = async () => {
      try {
        await deleteAction({id: deleteId.value})
        if (deleteData.value) {
          await loadOrders()
          toggleDeleteModal(0)
        }
      } catch {
        console.error(deleteError.value)
      }
    }
    const deleteErrorText = computed(() => {
      if (deleteError.value?.error) {
        switch (deleteError.value.error) {
          case 'allowed time for cancel expired':
            return 'Разрешенное время для отмены истекло'
          case 'cancellation has already been applied':
            return 'Отмена уже была применена'
          default:
            return deleteError.value.error
        }
      }
    })

    const  convertDate = (dateString) => {
      const offset = dateString.getTimezoneOffset()
      return new Date(dateString.getTime() - (offset*60*1000)).toISOString().split('T')[0]
    }
    const errorForm = ref(null)
    const createItem = async () => {
      errorForm.value = null
      try {
        await createAction({
          active_until: convertDate(fields.active_until),
          value: Number(fields.value),
          count: Number(fields.count),
          geo_restrictions: [
            {
              max_intermediate_points: Number(fields.geo_restrictions[0].max_intermediate_points),
              source: {
                geo_restriction_id: fields.geo_restrictions[0].source.geo_restriction_id,
              },
              destination: {
                geo_restriction_id: fields.geo_restrictions[0].destination.geo_restriction_id,
              },
            }
          ]
        })
        if (createData.value) {
          await loadOrders()
          toggleCreateModal()
        }
      } catch {
        if (createError.value?.data) {
          errorForm.value = {...createError.value.data}
        }
      }
    }

    const formatTableData = (data) => {
      const [year, month, day] = data.split('-');
      return `${day}.${month}.${year}`;
    }

    const checkActive = (date) => {
      return new Date(date) >= today.value
    }

    onBeforeMount(async () => {
      await loadOrders()
      await loadGeos()
      await nextTick(() => {
        window.scrollTo(0, 0)
      })
    })

    return {
      t,
      orders,
      deleteErrorText,
      deleteItem,
      createItem,
      toggleCreateModal,
      isGeoLoading,
      isOrdersLoading,
      isCreateLoading,
      isDeleteLoading,
      visibleDeleteModal,
      visibleCreateModal,
      errorForm,
      deleteId,
      toggleDeleteModal,
      fields,
      today,
      getNameService,
      isValid,
      georRestrictions,
      types,
      currentType,
      formatTableData,
      createError,
      checkActive,
    };
  }
});
</script>

<template>
  <ConfirmationModal v-if="visibleDeleteModal" v-model:visible="visibleDeleteModal">
    <template #header>
      <span>Удалить заказ</span>
    </template>

    <template #default>
      <span class="flex mt-2">Вы уверены, что хотите удалить заказ?</span>
    </template>

    <template #footer>
      <span v-if="deleteErrorText" class="flex color-red">{{ deleteErrorText }}</span>
      <div class="flex justify-content-between mt-4">
        <Button :label="t('labels.cancel')" @click="toggleDeleteModal(0)"
                class="btn-primary-outlined font-light w-12"/>
        <Button :loading="isDeleteLoading" :disabled="isDeleteLoading" :label="t('labels.destroy')" @click="deleteItem"
                class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>
  <ConfirmationModal v-model:visible="visibleCreateModal" closable @close-modal="toggleCreateModal" class="rules__popup">
    <template #header>
      Создать заказ
    </template>

    <template #default>
      <div class="promos__popup-form">
        <div class="promos__popup-grid">
          <div class="promos__popup-wrapper">
          <span class="p-float-label w-full">
             <InputText
                 v-model="fields.value"
                 id="name_ru"
                 class="w-full"
                 type="number"
             />
           <label for="name_ru">Введите номинал заказа</label>
          </span>
            <span v-if="errorForm?.value" class="promos__popup-error color-red">{{errorForm?.value[0] || ''}}</span>
          </div>
          <div class="promos__popup-wrapper">
          <span class="p-float-label w-full">
             <InputText
                 v-model="fields.count"
                 id="name_ru"
                 class="w-full"
                 type="number"
             />
           <label for="name_ru">Введите количество</label>
          </span>
            <span v-if="errorForm?.count" class="promos__popup-error color-red">{{errorForm?.count[0] || ''}}</span>
          </div>
        </div>
        <div class="promos__popup-grid">
          <div class="promos__popup-wrapper">
            <span class="p-float-label w-full">
               <InputText
                   v-model="fields.name"
                   id="name_ru"
                   class="w-full"
               />
             <label for="name_ru">Введите название заказа</label>
            </span>
            <span v-if="errorForm?.name" class="promos__popup-error color-red">{{errorForm?.name[0] || ''}}</span>
          </div>
          <div class="promos__popup-wrapper">
            <span class="p-float-label w-full">
              <span class="p-float-label">
                <Dropdown
                    inputId="type"
                    disabled
                    v-model="currentType"
                    :options="types"
                    optionLabel="name"
                    optionValue="type"
                    class="w-full border-radius-15"
                />
                <label for="type">Сервис</label>
              </span>
            </span>
          </div>
        </div>
        <div class="promos__popup-grid">
          <div class="promos__popup-wrapper">
            <span class="p-float-label w-full">
              <span class="p-float-label relative">
                <Calendar
                    v-model="today"
                    disabled
                    date-format="dd.mm.yy"
                    placeholder="Начало действия"
                    showButtonBar
                    :min-date="today"
                    class="w-full" />
                <label for="active_until">Начало действия</label>
                <i class="pi pi-calendar calendar-icon" />
              </span>
            </span>
          </div>
          <div class="promos__popup-wrapper">
            <span class="p-float-label w-full">
              <span class="p-float-label relative promos__popup-calendar">
                <Calendar
                    id="active_until"
                    v-model="fields.active_until"
                    date-format="dd.mm.yy"
                    placeholder="Конец действия"
                    showButtonBar
                    :min-date="today"
                    class="w-full" />
                <label for="active_until">Конец действия</label>
                <i class="pi pi-calendar calendar-icon" />
              </span>
            </span>
            <span v-if="errorForm?.active_until" class="promos__popup-error color-red">{{errorForm?.active_until[0] || ''}}</span>
          </div>
        </div>

        <div class="promos__popup-grid">
          <span class="promos__popup-grid-title">Географические ограничения:</span>
          <div class="promos__popup-wrapper">
            <span class="p-float-label w-full">
              <span class="p-float-label">
                <Dropdown
                    inputId="source"
                    v-model="fields.geo_restrictions[0].source.geo_restriction_id"
                    :options="georRestrictions"
                    optionLabel="name"
                    optionValue="id"
                    class="w-full border-radius-15"
                />
                <label for="source">ID района начала</label>
              </span>
            </span>
          </div>
          <div class="promos__popup-wrapper">
            <span class="p-float-label w-full">
              <span class="p-float-label">
                <Dropdown
                    inputId="destination"
                    v-model="fields.geo_restrictions[0].destination.geo_restriction_id"
                    :options="georRestrictions"
                    optionLabel="name"
                    optionValue="id"
                    class="w-full border-radius-15"
                />
                <label for="destination">ID района конца</label>
              </span>
            </span>
          </div>
        </div>
        <div class="promos__popup-wrapper">
          <span class="p-float-label w-full">
             <InputText
                 v-model="fields.geo_restrictions[0].max_intermediate_points"
                 id="max_intermediate_points"
                 class="w-full"
                 type="number"
             />
           <label for="max_intermediate_points">Максимальное количество промежуточных точек</label>
          </span>
          <span v-if="errorForm?.max_intermediate_points" class="promos__popup-error color-red">{{errorForm?.max_intermediate_points[0] || ''}}</span>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex flex-column gap-3">
        <span v-if="createError && !createError.data" class="promos__popup-error color-red">{{ createError.error }}</span>
        <Button :loading="isCreateLoading" :disabled="isCreateLoading || !isValid" label="Сохранить" @click="createItem"
                class="btn-primary promos__header-btn font-light"/>
      </div>
    </template>
  </ConfirmationModal>
  <section class="promos__header">
      <h1 class="promos__header-title">Промокоды</h1>
      <div class="flex gap-3">
        <Button label="Создать заказ" @click="toggleCreateModal" class="btn-primary promos__header-btn"/>
      </div>
  </section>

  <section class="promos__body">
    <div class="promos__body-table">
      <DataTable :value="orders" :loading="isOrdersLoading" showGridlines tableStyle="min-width: 50rem" selectionMode="single">
        <Column field="name_ru" header="Идентификатор заказа">
          <template #body="slotProps">
            <button class="promos__body-table-name">
              <span>{{ slotProps.data.order_id }}</span>
            </button>
          </template>
        </Column>

        <Column field="id" header="Дата окончания">
          <template #body="slotProps">
            <div class="promos__body-table-name">
              {{ formatTableData(slotProps.data?.active_until) }}
            </div>
          </template>
        </Column>
        <Column field="id" header="Количество промокодов в серии">
          <template #body="slotProps">
            <div class="promos__body-table-name">
              {{ slotProps.data?.count }}
            </div>
          </template>
        </Column>
<!--        <Column field="id" header="Количество примененных промокодов">-->
<!--          <template #body="slotProps">-->
<!--            <div class="promos__body-table-name">-->
<!--              {{ slotProps.data?.revoke_count }}-->
<!--            </div>-->
<!--          </template>-->
<!--        </Column>-->
        <Column field="id" header="Цена">
          <template #body="slotProps">
            <div class="promos__body-table-name">
              {{ Math.trunc(slotProps.data?.pricing?.cost_with_vat) }} ₽
            </div>
          </template>
        </Column>
        <Column field="id" header="Сервис">
          <template #body="slotProps">
            <div class="promos__body-table-name">
              {{ getNameService(slotProps.data?.service) }}
            </div>
          </template>Номинал
        </Column>
        <Column field="id" header="Номинал">
          <template #body="slotProps">
            <div class="promos__body-table-name">
              {{ slotProps.data?.value }} ₽
            </div>
          </template>
        </Column>
        <Column field="text" header="Действия">
          <template #body="slotProps">
            <div class="promos__body-table-actions">
              <button v-if="slotProps.data.status !== 'cancellation_success' && slotProps.data.status !== 'pending' && checkActive(slotProps.data.active_until)"  @click="toggleDeleteModal(slotProps.data.order_id)" class="promos__body-table-delete">
                <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.2679 5.36049H14.7322C14.7322 4.76849 14.497 4.20074 14.0784 3.78213C13.6598 3.36352 13.092 3.12835 12.5 3.12835C11.908 3.12835 11.3403 3.36352 10.9217 3.78213C10.503 4.20074 10.2679 4.76849 10.2679 5.36049ZM8.92859 5.36049C8.92859 4.41329 9.30486 3.50488 9.97464 2.83511C10.6444 2.16534 11.5528 1.78906 12.5 1.78906C13.4472 1.78906 14.3556 2.16534 15.0254 2.83511C15.6952 3.50488 16.0714 4.41329 16.0714 5.36049H21.6518C21.8294 5.36049 21.9997 5.43104 22.1253 5.55662C22.2509 5.68221 22.3214 5.85253 22.3214 6.03013C22.3214 6.20773 22.2509 6.37806 22.1253 6.50364C21.9997 6.62923 21.8294 6.69978 21.6518 6.69978H20.4822L19.3956 19.7382C19.3165 20.6867 18.8839 21.5708 18.1835 22.2153C17.4831 22.8598 16.5661 23.2176 15.6143 23.2176H9.38573C8.43394 23.2176 7.51693 22.8598 6.81654 22.2153C6.11615 21.5708 5.68354 20.6867 5.60448 19.7382L4.51787 6.69978H3.34823C3.17063 6.69978 3.00031 6.62923 2.87472 6.50364C2.74914 6.37806 2.67859 6.20773 2.67859 6.03013C2.67859 5.85253 2.74914 5.68221 2.87472 5.55662C3.00031 5.43104 3.17063 5.36049 3.34823 5.36049H8.92859ZM6.9393 19.6266C6.99037 20.2403 7.27022 20.8125 7.72336 21.2296C8.17651 21.6467 8.76985 21.8782 9.38573 21.8783H15.6143C16.2302 21.8782 16.8235 21.6467 17.2767 21.2296C17.7298 20.8125 18.0097 20.2403 18.0607 19.6266L19.1393 6.69978H5.86162L6.9393 19.6266ZM10.4911 9.82478C10.6687 9.82478 10.839 9.89533 10.9646 10.0209C11.0902 10.1465 11.1607 10.3168 11.1607 10.4944V18.0837C11.1607 18.2613 11.0902 18.4316 10.9646 18.5572C10.839 18.6828 10.6687 18.7533 10.4911 18.7533C10.3135 18.7533 10.1432 18.6828 10.0176 18.5572C9.892 18.4316 9.82145 18.2613 9.82145 18.0837V10.4944C9.82145 10.3168 9.892 10.1465 10.0176 10.0209C10.1432 9.89533 10.3135 9.82478 10.4911 9.82478ZM15.1786 10.4944C15.1786 10.3168 15.108 10.1465 14.9825 10.0209C14.8569 9.89533 14.6865 9.82478 14.5089 9.82478C14.3313 9.82478 14.161 9.89533 14.0354 10.0209C13.9099 10.1465 13.8393 10.3168 13.8393 10.4944V18.0837C13.8393 18.2613 13.9099 18.4316 14.0354 18.5572C14.161 18.6828 14.3313 18.7533 14.5089 18.7533C14.6865 18.7533 14.8569 18.6828 14.9825 18.5572C15.108 18.4316 15.1786 18.2613 15.1786 18.0837V10.4944Z" fill="#E26B6B"/>
                </svg>
              </button>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </section>
</template>

<style lang="scss">
th:nth-child(3),
th:nth-child(4) {
  max-width: 200px;
}
.promos__header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 20px;
  padding-top: 14px;
  margin-bottom: 24px;
}
.promos__header-btn {
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  min-width: 228px;
  height: 46px;
  color: white;
  background: #C3987A;
}
.promos__body-table {
  .p-column-header-content  {
    justify-content: start !important;
    .p-column-title {
      font-weight: 600;
      font-size: 18px;
      line-height: 100%;
      text-align: start;
      color: #333333;
    }
  }
}

.promos__body-table-name,
.promos__body-table-permissions {
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  text-align: left;
  color: #333333;
}
.promos__body-table-name {
  display: flex;
  justify-content: start;
  text-decoration: unset;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 4px;
}
.promos__body-table-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
//.promos__body-table-actions:before {
//  position: absolute;
//  content: '';
//  width: 1px;
//  height: 25px;
//  background: #E1E8ED;
//  top: 50%;
//  left: calc(50% - 0.5px);
//  transform: translateY(-50%);
//  z-index: 2;
//}
.promos__body-table-delete,
.promos__body-table-edit {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
}
.promos__popup-wrapper {
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 10px;
  width: 100%;
}
.promos__popup-error {
  font-size: 10px;
}
.rules__popup {
  max-width: unset !important;
  .p-dialog-header-icons {
    position: absolute;
    right: 8px;
    top: 8px;
  }
  .p-dialog-header {
    padding: 20px 20px 0 20px !important;
  }
  .p-dialog-content {
    padding: 0 20px 20px;
  }
  .p-dialog-footer {
    padding: 0 20px 20px;
    justify-content: start;
    text-align: start !important;
  }
  &.--list {
    max-width: 940px !important;
    width: 100%;

    .p-dialog-content {
      padding: 0 !important;
    }
    .p-dialog-footer {
      padding-top: 20px !important;
    }
    label {
      font-weight: 400;
      font-size: 18px;
      line-height: 100%;
      color: #333333;
      cursor: pointer;
    }
  }
}
.promos__popup-content {
  display: grid;
  gap: 1px;
  background: #E1E8ED;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid #E1E8ED;
  border-bottom: 1px solid #E1E8ED;
}
.promos__popup-content-item {
  padding: 16px 20px;
  background: white;
}
.promos__popup-content-error {
  padding: 16px 20px;
}
.promos__popup-form {
  width: 600px;
  padding: 25px 0 0;
  display: flex;
  flex-direction: column;
  gap: 25px;
}
.promos__popup-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  .promos__popup-wrapper {
    max-width: calc(50% - 10px);
  }
}
.promos__popup-grid-title {
  display: flex;
  width: 100%;
  margin-bottom: 5px;
}
.calendar-icon {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}
.p-calendar {
  border-radius: 4px !important;
  input[role="combobox"] {
    border-radius: 4px !important;
  }
}
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>